import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell, CellState } from '@/grid/Cell';
import { PriorityQueue } from '@/utils/PriorityQueue';

export class Dijkstra extends PathFinder {
  readonly name = 'Dijkstra';
  readonly description = 'Uniform-cost search guaranteeing shortest path';

  findPath(grid: BaseGrid, start: Cell, goal: Cell, options?: AlgorithmOptions): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    _options?: AlgorithmOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();

    grid.reset();

    const openSet = new PriorityQueue<Cell>();
    const closedSet = new Set<Cell>();

    start.gCost = 0;
    start.parent = null;

    openSet.enqueue(start, start.gCost);
    start.state = CellState.OPEN;

    yield {
      type: 'open',
      cell: start,
      cost: start.gCost,
      openListSize: openSet.size,
      closedListSize: closedSet.size
    };

    let nodesExplored = 0;

    while (!openSet.isEmpty()) {
      const current = openSet.dequeue();
      if (!current) break;

      if (current.equals(goal)) {
        const path = this.reconstructPath(goal);
        const executionTime = performance.now() - startTime;

        yield {
          type: 'path_found',
          cell: current,
          cost: current.gCost,
          openListSize: openSet.size,
          closedListSize: closedSet.size
        };

        return new PathResult(path, nodesExplored, executionTime, true);
      }

      closedSet.add(current);
      current.state = CellState.CLOSED;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        cost: current.gCost,
        openListSize: openSet.size,
        closedListSize: closedSet.size
      };

      const neighbors = grid.getNeighbors(current);

      for (const neighbor of neighbors) {
        if (closedSet.has(neighbor)) {
          continue;
        }

        const tentativeGCost = current.gCost + grid.calculateDistance(current, neighbor) * neighbor.weight;

        if (tentativeGCost < neighbor.gCost) {
          neighbor.parent = current;
          neighbor.gCost = tentativeGCost;

          const isInOpenSet = openSet.contains(neighbor, (a, b) => a.equals(b));

          if (isInOpenSet) {
            openSet.updatePriority(neighbor, neighbor.gCost, (a, b) => a.equals(b));
          } else {
            openSet.enqueue(neighbor, neighbor.gCost);
            neighbor.state = CellState.OPEN;

            yield {
              type: 'open',
              cell: neighbor,
              cost: neighbor.gCost,
              parent: current,
              openListSize: openSet.size,
              closedListSize: closedSet.size
            };
          }
        }
      }
    }

    const executionTime = performance.now() - startTime;
    return PathResult.failure(nodesExplored, executionTime);
  }
}
